/**
 * The saved replies: a list on the left, one editor on the right.
 *
 * Two panes rather than a list of inline forms, because these are written once
 * and read often - the editor is the rare visit and the list is the common one.
 * Deleting asks first: a canned reply is somebody's paragraph, and there is no
 * undo behind this endpoint.
 */
import { useCallback, useEffect, useState } from "react";
import { BookText, Loader2, Plus, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  createCannedReply, deleteCannedReply, getCannedReplies, updateCannedReply, type CannedReply,
} from "../../../lib/support";
import { Btn, EmptyState, Modal } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";

export function CannedRepliesModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [rows, setRows] = useState<CannedReply[] | null>(null);
  const [editing, setEditing] = useState<CannedReply | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState<CannedReply | null>(null);

  const load = useCallback(() => {
    getCannedReplies()
      .then((r) => setRows(r.rows))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => load(), [load]);

  function startEdit(c: CannedReply | null) {
    setEditing(c);
    setTitle(c?.title ?? "");
    setBody(c?.body ?? "");
  }

  async function save() {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      if (editing) await updateCannedReply(editing.id, { title: title.trim(), body: body.trim() });
      else await createCannedReply({ title: title.trim(), body: body.trim() });
      startEdit(null);
      load();
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: CannedReply) {
    try {
      await deleteCannedReply(c.id);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== c.id));
      // The editor was holding the row that is now gone; leaving it open would
      // offer to save an edit to a record the server no longer has.
      if (editing?.id === c.id) startEdit(null);
    } finally {
      setConfirmDel(null);
    }
  }

  return (
    <Modal
      title={t("support.cannedReplies")}
      label={t("support.cannedReplies")}
      onClose={onClose}
      /* `width`, not `max-width`: `.crm-modal` sets `width: min(520px, 100%)`,
         so a `max-w-` above 520 is dead code - it can only ever narrow a
         dialog. Two panes side by side need the room. */
      className="w-[min(760px,100%)]"
    >
      <div className="mt-1 grid gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <span className="crm-lab !mt-0">{t("support.cannedList")}</span>
          <div className="max-h-[42vh] min-h-[12rem] overflow-y-auto rounded-[22px] bg-[var(--crm-sunk)] p-2">
            {rows === null ? (
              <div className="flex justify-center py-10 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
              </div>
            ) : rows.length === 0 ? (
              <EmptyState icon={BookText} title={t("support.cannedEmpty")} />
            ) : (
              <ul className="flex flex-col gap-1.5">
                {rows.map((c) => (
                  <li key={c.id} className="crm-prow gap-2 p-2.5">
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => startEdit(c)}>
                      <span className="crm-prow-n crm-trunc block">{c.title}</span>
                      <span className="crm-prow-s crm-trunc block">{c.body}</span>
                    </button>
                    <button
                      type="button"
                      className="crm-cb crm-cb-sm"
                      onClick={() => setConfirmDel(c)}
                      aria-label={t("common.delete")}
                      title={t("common.delete")}
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="min-w-0">
          <span className="crm-lab !mt-0">{editing ? t("support.cannedEdit") : t("support.cannedNew")}</span>
          <label className="crm-inp">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("support.cannedTitle")}
              autoComplete="off"
            />
          </label>
          <textarea
            className="crm-area mt-3"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder={t("support.cannedBody")}
          />
          <div className="mt-3 flex justify-end gap-2">
            {editing ? (
              <Btn small onClick={() => startEdit(null)}>
                {t("support.cannedNewBtn")}
              </Btn>
            ) : null}
            <Btn small tone="brand" onClick={() => void save()} disabled={busy || !title.trim() || !body.trim()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t("common.save")}
            </Btn>
          </div>
        </div>
      </div>

      <div className="crm-m-foot">
        <Btn type="button" onClick={onClose}>
          {t("common.close")}
        </Btn>
      </div>

      {confirmDel ? (
        <ConfirmModal
          title={t("support.cannedDeleteTitle")}
          body={t("support.cannedDeleteConfirm", { title: confirmDel.title })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </Modal>
  );
}
